import * as React from "react";
import { useResearchChats } from "../../hooks/useChats";
import { useChat } from "../../hooks/useChat";
import { useCreateChat } from "../../hooks/useCreateChat";
import { useDocument } from "../../hooks/useDocument";
import { getBackendDocumentId } from "@/utils/document-id.utils";
import type { Chat, ResearchChatData } from "@/types/research.types";
const ChatDetail = React.lazy(() => import("./ChatDetail"));
import { Button } from "./ui/button";
import { InputGroup, InputGroupAddon, InputGroupInput } from "./ui/input-group";
import { Loader, ArrowLeft, Search, Plus } from "lucide-react";
const ResearchChatList = React.lazy(() => import("./ResearchChatList"));
import { ErrorBoundary } from "./ErrorBoundary";

interface ResearchProps {
  onNewMessage?: () => void;
  isActive?: boolean;
}

const LoadingState: React.FC = () => (
  <div className="flex items-center justify-center flex-1 min-h-0">
    <Loader className="animate-spin" />
    <span className="ml-2">Loading chats...</span>
  </div>
);

const NoDocumentState: React.FC = () => (
  <div className="flex flex-col items-center justify-center flex-1 min-h-0 p-8 text-center">
    <p className="text-sm text-neutral-500">
      No document loaded. Please create or open a document to use research chats.
    </p>
  </div>
);

const ErrorState: React.FC = () => (
  <div className="flex flex-col items-center justify-center flex-1 min-h-0 p-8 text-center">
    <p className="text-sm text-danger-200">Failed to load chats. Please try again.</p>
  </div>
);

const SuspenseFallback: React.FC = () => (
  <div className="flex justify-center items-center h-[200px]">
    <Loader className="animate-spin" />
    <span className="ml-2">Loading...</span>
  </div>
);

const ChatDetailView: React.FC<{
  selectedChatId: string;
  chatName: string | undefined;
  selectedChat: ResearchChatData | undefined;
  isLoadingChat: boolean;
  onBack: () => void;
  onNewMessage?: () => void;
  isActive: boolean;
}> = ({
  selectedChatId,
  chatName,
  selectedChat,
  isLoadingChat,
  onBack,
  onNewMessage,
  isActive,
}) => (
  <div className="flex flex-col flex-1 min-h-0 relative">
    <div className="flex gap-3 items-center mb-1">
      <Button
        variant="ghost"
        className="size-auto p-0"
        size="icon"
        onClick={onBack}
        title="Back to chat list"
      >
        <ArrowLeft className="w-5 h-5" />
      </Button>
      <p className="font-medium text-sm flex-1 truncate">{chatName ?? "Loading..."}</p>
    </div>
    <div className="flex-1 min-h-0 flex flex-col">
      {/* Suspense only covers a PENDING lazy import. If the chunk fails to
          load, or ChatDetail throws while rendering, the rejection goes to the
          nearest error boundary — and without one the entire task pane went
          blank white with no way back. */}
      <ErrorBoundary
        area="research-chat"
        title="This chat couldn't be opened"
        resetKey={selectedChatId}
      >
        <React.Suspense fallback={<SuspenseFallback />}>
          <ChatDetail
            messages={selectedChat?.prev_chat ?? []}
            isLoading={isLoadingChat}
            chatId={selectedChatId}
            onNewMessage={onNewMessage}
            isActive={isActive}
          />
        </React.Suspense>
      </ErrorBoundary>
    </div>
  </div>
);

const ChatListView: React.FC<{
  searchQuery: string;
  onSearchChange: (value: string) => void;
  onCreateChat: () => void;
  isCreating: boolean;
  filteredChats: Chat[];
  onChatClick: (chatId: string) => void;
}> = ({ searchQuery, onSearchChange, onCreateChat, isCreating, filteredChats, onChatClick }) => (
  <div className="flex flex-col flex-1 min-h-0 gap-3 relative">
    <div className="flex gap-2 items-center">
      <InputGroup className="flex-1 has-[[data-slot=input-group-control]:focus-visible]:ring-0">
        <InputGroupAddon>
          <Search className="size-4 text-neutral-500" />
        </InputGroupAddon>
        <InputGroupInput
          placeholder="Search chat by name"
          className="placeholder:text-neutral-500"
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
        />
      </InputGroup>
      <Button
        variant="default"
        size="icon"
        onClick={onCreateChat}
        disabled={isCreating}
        title="Create new chat"
      >
        {isCreating ? <Loader className="size-4 animate-spin" /> : <Plus className="size-4" />}
      </Button>
    </div>
    <div className="flex-1 min-h-0 flex flex-col">
      <ErrorBoundary area="research-chat-list" title="Your chats couldn't be listed">
        <React.Suspense fallback={<SuspenseFallback />}>
          <ResearchChatList
            chats={filteredChats}
            searchQuery={searchQuery}
            onChatClick={onChatClick}
          />
        </React.Suspense>
      </ErrorBoundary>
    </div>
  </div>
);

const Research: React.FC<ResearchProps> = ({ onNewMessage, isActive = true }) => {
  const { data: document, isLoading: isLoadingDocument } = useDocument();
  const documentId = getBackendDocumentId(document);

  const { data: chats, isLoading: isLoadingChats, error } = useResearchChats(documentId);

  const isLoading = isLoadingDocument || isLoadingChats;

  const createChatMutation = useCreateChat();

  const [selectedChatId, setSelectedChatId] = React.useState<string | null>(null);
  const [searchQuery, setSearchQuery] = React.useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = React.useState("");

  const { data: selectedChat, isLoading: isLoadingChat } = useChat(selectedChatId ?? "");

  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const filteredChats = React.useMemo(() => {
    // Belt and braces: the list endpoint is normalised in research.service, but
    // an unexpected shape here must never be able to blank the task pane again.
    // Spreading a non-array silently yields [] and .find() on one throws.
    if (!Array.isArray(chats)) return [];
    const reversedChats = [...chats].reverse();
    if (!debouncedSearchQuery.trim()) return reversedChats;
    return reversedChats.filter((chat) =>
      (chat.chat_name ?? "").toLowerCase().includes(debouncedSearchQuery.toLowerCase())
    );
  }, [chats, debouncedSearchQuery]);

  const selectedChatInfo = React.useMemo(() => {
    if (selectedChatId == null || !Array.isArray(chats)) return null;
    return chats.find((chat) => chat.chat_id === selectedChatId);
  }, [selectedChatId, chats]);

  const handleCreateChat = () => {
    createChatMutation.mutate(
      { chat_name: "New Chat", document_id: documentId },
      {
        onSuccess: (newChat) => {
          setSelectedChatId(newChat.chat_id);
        },
      }
    );
  };

  if (isLoading) return <LoadingState />;
  if (documentId == null) return <NoDocumentState />;
  if (error) return <ErrorState />;

  if (selectedChatId != null) {
    return (
      <ChatDetailView
        selectedChatId={selectedChatId}
        chatName={selectedChatInfo?.chat_name}
        selectedChat={selectedChat}
        isLoadingChat={isLoadingChat}
        onBack={() => setSelectedChatId(null)}
        onNewMessage={onNewMessage}
        isActive={isActive}
      />
    );
  }

  return (
    <ChatListView
      searchQuery={searchQuery}
      onSearchChange={setSearchQuery}
      onCreateChat={handleCreateChat}
      isCreating={createChatMutation.isPending}
      filteredChats={filteredChats}
      onChatClick={setSelectedChatId}
    />
  );
};

export default Research;
